import { buildPinQueryParams } from "src/analytics/dashboard/dashboardFilterParams";
import type { MetricsQueryRow } from "src/analytics/metrics/useMetricsQuery";
import { extractTitle } from "src/chartSandbox/SandboxedChart";
import type { ChartDefinition, Pin } from "src/clients/analytics-dashboard";
import type { Environment } from "src/hooks/useEnvironment";
import type { StrictDateRange } from "src/utils/timeWindow";

// Cap embedded rows so a large chart can't bloat the invoke payload.
export const CHART_CONTEXT_ROW_CAP = 500;

export type ExplorePin = Pick<ChartDefinition, "chart_definition"> & {
  pin_id: Pin["id"];
  name: string;
  // Only the parameters the pin's query actually binds (see buildPinQueryParams).
  parameters: {
    environment?: Environment[];
    time_range_start?: string;
    time_range_end?: string;
  };
  query: ChartDefinition["query"]["query"];
  data: MetricsQueryRow[];
};

export type ChartExploreContext = {
  pins: ExplorePin[];
};

export const pinDisplayTitle = (pin: Pin): string =>
  pin.title ||
  extractTitle(pin.definition.chart_definition as Record<string, unknown>);

export const buildExplorePin = (
  pin: Pin,
  rows: MetricsQueryRow[] | undefined,
  environment: Environment,
  timeWindow: StrictDateRange,
): ExplorePin => ({
  pin_id: pin.id,
  name: pinDisplayTitle(pin),
  parameters:
    buildPinQueryParams(
      pin.definition.query.parameters,
      environment,
      timeWindow,
    ) ?? {},
  query: pin.definition.query.query,
  chart_definition: pin.definition.chart_definition as Record<string, unknown>,
  data: (rows ?? []).slice(0, CHART_CONTEXT_ROW_CAP),
});

export const mergeExplorePins = (
  ...contexts: (ChartExploreContext | null | undefined)[]
): ChartExploreContext | null => {
  const pins = [
    ...new Map(
      contexts.flatMap((c) => c?.pins ?? []).map((pin) => [pin.pin_id, pin]),
    ).values(),
  ];
  return pins.length > 0 ? { pins } : null;
};
